import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, MessageSquare, Users, Zap, Database } from "lucide-react";

function Stat({ label, value, hint, color = "text-[#123B5D]", testId }) {
    return (
        <Card data-testid={testId}>
            <CardContent className="pt-4">
                <div className="text-[11px] uppercase tracking-widest text-[#17212B]/50">{label}</div>
                <div className={`text-2xl font-bold ${color}`}>{value ?? "—"}</div>
                {hint && <div className="text-[11px] text-[#17212B]/60 mt-0.5">{hint}</div>}
            </CardContent>
        </Card>
    );
}

function Bar({ label, count, max, color = "#0F766E" }) {
    const w = max > 0 ? Math.round((count / max) * 100) : 0;
    return (
        <div className="text-xs mb-1">
            <div className="flex justify-between mb-0.5"><span>{label}</span><span className="font-bold">{count}</span></div>
            <div className="w-full bg-[#F5F7FB] rounded h-1.5 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${w}%`, background: color }} />
            </div>
        </div>
    );
}

// ── SMS tab ──────────────────────────────────────────────────────────────
function SmsDash() {
    const [days, setDays] = useState(7);
    const [d, setD] = useState(null);
    const load = () => api.get(`/dashboards/sms?days=${days}`).then((r) => setD(r.data)).catch(() => toast.error("Failed"));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
    if (!d) return <div className="text-xs text-[#17212B]/60">Loading…</div>;
    const maxStatus = Math.max(...Object.values(d.by_status || {}), 1);
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <span className="text-xs text-[#17212B]/60">Range:</span>
                <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                    <SelectTrigger className="w-28 h-9" data-testid="dash-sms-days"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {[1, 7, 14, 30, 60, 90].map((n) => <SelectItem key={n} value={String(n)}>{n} days</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat testId="dash-sms-total" label="Total messages" value={d.total} />
                <Stat testId="dash-sms-delivered" label="Delivered" value={d.by_status?.DELIVERED} color="text-emerald-700" />
                <Stat testId="dash-sms-failed" label="Failed" value={d.by_status?.FAILED} color="text-red-600" />
                <Stat testId="dash-sms-queued" label="Queued" value={d.by_status?.QUEUED} color="text-amber-700" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Card><CardContent className="pt-4">
                    <h3 className="text-sm font-bold text-[#123B5D] mb-3">By status</h3>
                    {Object.entries(d.by_status || {}).map(([k, v]) => (
                        <Bar key={k} label={k} count={v} max={maxStatus} color={k === "FAILED" ? "#DC2626" : k === "DELIVERED" ? "#15803D" : "#0F766E"} />
                    ))}
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                    <h3 className="text-sm font-bold text-[#123B5D] mb-3">Cost by provider</h3>
                    {(d.cost_by_provider || []).length ? d.cost_by_provider.map((r, i) => (
                        <div key={i} className="flex justify-between text-xs border-b py-1">
                            <span>{r.provider_code}</span>
                            <span><b>{r.count}</b> msgs · ₹{r.cost}</span>
                        </div>
                    )) : <div className="text-[11px] text-[#17212B]/50">No cost data yet.</div>}
                </CardContent></Card>
            </div>
            <Card><CardContent className="pt-4">
                <h3 className="text-sm font-bold text-[#123B5D] mb-3">Top failure codes</h3>
                {(d.top_errors || []).length ? d.top_errors.map((e, i) => (
                    <div key={i} className="flex justify-between text-xs border-b py-1">
                        <span className="font-mono text-[11px]">{e._id || "(no code)"}</span>
                        <span className="font-bold">{e.count}</span>
                    </div>
                )) : <div className="text-[11px] text-[#17212B]/50">Zero failures — nice.</div>}
            </CardContent></Card>
        </div>
    );
}

// ── CRM tab ──────────────────────────────────────────────────────────────
function CrmDash() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get(`/dashboards/crm`).then((r) => setD(r.data)).catch(() => toast.error("Failed")); }, []);
    if (!d) return <div className="text-xs text-[#17212B]/60">Loading…</div>;
    const maxStage = Math.max(...d.stage_counts.map((s) => s.count), 1);
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat testId="dash-crm-total" label="Total records" value={d.total_records} />
                <Stat testId="dash-crm-new-week" label="New this week" value={d.new_this_week} color="text-emerald-700" />
                <Stat testId="dash-crm-overdue" label="Overdue tasks" value={d.overdue_tasks} color="text-amber-700" />
                <Stat testId="dash-crm-dups" label="Duplicates pending" value={d.dup_pending} color="text-red-600" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Card><CardContent className="pt-4">
                    <h3 className="text-sm font-bold text-[#123B5D] mb-3">Funnel (records per stage)</h3>
                    {d.stage_counts.map((s) => (
                        <Bar key={s.stage} label={s.label} count={s.count} max={maxStage} color={s.color} />
                    ))}
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                    <h3 className="text-sm font-bold text-[#123B5D] mb-3">Score bands</h3>
                    {["hot", "warm", "cold"].map((b) => (
                        <div key={b} className="flex justify-between text-xs border-b py-1">
                            <span className="capitalize">{b}</span>
                            <span className="font-bold">{d.score_bands?.[b] || 0}</span>
                        </div>
                    ))}
                    <h3 className="text-sm font-bold text-[#123B5D] mt-4 mb-3">Tag distribution (top 10)</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {(d.tag_distribution || []).length ? d.tag_distribution.map((t, i) => (
                            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                                style={{ background: `${t.color}22`, color: t.color }}>
                                {t.tag} · {t.count}
                            </span>
                        )) : <span className="text-[11px] text-[#17212B]/50">No tags attached.</span>}
                    </div>
                </CardContent></Card>
            </div>
        </div>
    );
}

// ── Automation tab ───────────────────────────────────────────────────────
function AutomationDash() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get(`/dashboards/automation?days=7`).then((r) => setD(r.data)).catch(() => {}); }, []);
    if (!d) return <div className="text-xs text-[#17212B]/60">Loading…</div>;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat testId="dash-auto-active" label="Active rules" value={d.active_rules} />
                <Stat testId="dash-auto-total" label="Total fires (7d)" value={d.total_fires} />
                <Stat testId="dash-auto-matched" label="Matched" value={d.matched_fires} color="text-emerald-700" />
                <Stat testId="dash-auto-errors" label="With error" value={d.with_error} color="text-red-600" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Card><CardContent className="pt-4">
                    <h3 className="text-sm font-bold text-[#123B5D] mb-3">Top firing rules</h3>
                    {(d.top_rules || []).length ? d.top_rules.map((r) => (
                        <div key={r._id} className="text-xs border-b py-1">
                            <div className="flex justify-between">
                                <span className="font-semibold">{r.name}</span>
                                <span>{r.fires} fires ({r.matched} matched)</span>
                            </div>
                            <div className="text-[10px] font-mono text-[#17212B]/50">{r.trigger_event}</div>
                        </div>
                    )) : <div className="text-[11px] text-[#17212B]/50">No fires in this range.</div>}
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                    <h3 className="text-sm font-bold text-[#123B5D] mb-3">Top events (7d)</h3>
                    {(d.top_events || []).length ? d.top_events.map((e, i) => (
                        <div key={i} className="flex justify-between text-xs border-b py-1">
                            <span className="font-mono text-[10px]">{e.event}</span>
                            <span className="font-bold">{e.count}</span>
                        </div>
                    )) : <div className="text-[11px] text-[#17212B]/50">No events yet.</div>}
                </CardContent></Card>
            </div>
        </div>
    );
}

// ── Retention tab ────────────────────────────────────────────────────────
function RetentionDash() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const load = () => api.get(`/dashboards/retention/policies`).then((r) => setItems(r.data.items || []));
    useEffect(() => { load(); }, []);
    const save = async (row, patch) => {
        try { await api.put(`/dashboards/retention/policies/${row.collection}`, patch); load(); toast.success("Policy saved"); }
        catch { toast.error("Failed"); }
    };
    const runPurge = async () => {
        if (!window.confirm("Run retention purge across all enabled policies? Data older than the retention window will be permanently deleted.")) return;
        setBusy(true);
        try {
            const r = await api.post(`/dashboards/retention/apply`);
            toast.success(`Purge applied — ${r.data.results.length} collections`);
            load();
        } catch { toast.error("Purge failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <p className="text-xs text-[#17212B]/60">Data retention policies control how long each collection's rows survive. Purge deletes rows older than <b>retain_days</b>.</p>
                <Button onClick={runPurge} disabled={busy} data-testid="dash-retention-apply">
                    <Database className="w-3.5 h-3.5 mr-1" />Run purge now
                </Button>
            </div>
            <Card><CardContent className="pt-4 overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="dash-retention-table">
                    <thead className="bg-[#123B5D] text-white text-xs uppercase">
                        <tr>
                            <th className="px-3 py-2 text-left">Collection</th>
                            <th className="px-3 py-2 text-left">Retain days</th>
                            <th className="px-3 py-2 text-left">Enabled</th>
                            <th className="px-3 py-2 text-left">Last purged</th>
                            <th className="px-3 py-2 text-left">Last count</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((r, i) => (
                            <tr key={r.collection} className={i % 2 ? "bg-[#F5F8FA]" : ""} data-testid={`dash-retention-row-${r.collection}`}>
                                <td className="px-3 py-2 font-mono text-[11px]">{r.collection}</td>
                                <td className="px-3 py-2">
                                    <input type="number" min="1" defaultValue={r.retain_days}
                                        onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v && v !== r.retain_days) save(r, { retain_days: v }); }}
                                        className="w-20 border rounded px-2 py-1 text-xs" data-testid={`dash-retention-days-${r.collection}`} />
                                </td>
                                <td className="px-3 py-2">
                                    <input type="checkbox" defaultChecked={r.enabled}
                                        onChange={(e) => save(r, { enabled: e.target.checked })}
                                        data-testid={`dash-retention-enabled-${r.collection}`} />
                                </td>
                                <td className="px-3 py-2 text-[11px]">{r.last_purged_at ? r.last_purged_at.slice(0, 16).replace("T", " ") : "—"}</td>
                                <td className="px-3 py-2 text-[11px]">{r.last_purge_count ?? "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </CardContent></Card>
        </div>
    );
}

// ── Main page ────────────────────────────────────────────────────────────
export default function SuperAdminDashboards() {
    const [tab, setTab] = useState("sms");
    return (
        <AdminShell>
            <div className="mb-5">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin · Ops dashboards</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Operations & Retention</h1>
                <p className="text-[#17212B]/60 mt-1 text-sm">SMS delivery health · CRM funnel · Automation rule fires · Data retention policies (Phase 5+6).</p>
            </div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="dashboards-tabs">
                    <TabsTrigger value="sms" data-testid="dash-tab-sms"><MessageSquare className="w-3.5 h-3.5 mr-1" />SMS</TabsTrigger>
                    <TabsTrigger value="crm" data-testid="dash-tab-crm"><Users className="w-3.5 h-3.5 mr-1" />CRM</TabsTrigger>
                    <TabsTrigger value="automation" data-testid="dash-tab-auto"><Zap className="w-3.5 h-3.5 mr-1" />Automation</TabsTrigger>
                    <TabsTrigger value="retention" data-testid="dash-tab-retention"><Database className="w-3.5 h-3.5 mr-1" />Retention</TabsTrigger>
                </TabsList>
                <TabsContent value="sms" className="mt-4"><SmsDash /></TabsContent>
                <TabsContent value="crm" className="mt-4"><CrmDash /></TabsContent>
                <TabsContent value="automation" className="mt-4"><AutomationDash /></TabsContent>
                <TabsContent value="retention" className="mt-4"><RetentionDash /></TabsContent>
            </Tabs>
        </AdminShell>
    );
}
